import {
  useDeleteProfileMutation,
  useGetProfileDetailsQuery,
  useGetProfileQuery,
  useGetProfileVisitHistoryQuery,
  usePostProfileMutation,
  useUpdateProfileMutation,
} from '@api/front-office/profile.api.rtk';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutoGraph,
  Business,
  EmailOutlined,
  HideSourceOutlined,
  HistoryOutlined,
  ListAlt,
  NoAccounts,
  PersonPinOutlined,
  PhoneInTalkOutlined,
  PrintOutlined,
  ReportProblem,
  Search,
  TrackChangesOutlined,
} from '@mui/icons-material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tooltip,
  tooltipClasses,
  TooltipProps,
  Typography,
} from '@mui/material';
import { styled, ThemeProvider } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { SHA512 } from 'crypto-js';
import moment from 'moment-timezone';
import { memo, useEffect, useMemo, useState } from 'react';
import {
  Controller,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';
import { useDeleteNetLockMutation } from '../../../../api/front-office/netlock.api.rtk';
import DrawerWrapper from '../../../../components/drawer/drawer-wrapper';
import AutoCompleteController from '../../../../components/form/controller/autocomplete.controller';
import DatePickerController from '../../../../components/form/controller/date-picker.controller';
import InputFieldsController from '../../../../components/form/controller/input-fields.controller';
import CustomNumberFormat, {
  CustomNumberFormatProps,
} from '../../../../components/form/custom-number-format';
import GroupTitle from '../../../../components/form/group-title';
import { DefaultSwitch } from '../../../../components/switch/default-switch';
import TracesListModal from '../../../../components/traces/traces-list-modal';
import ContainerTitle from '../../../../components/ui/container-title';
import useMenu from '../../../../hooks/useMenu';
import useToast from '../../../../hooks/useToast';
import { RootState, useAppSelector } from '../../../../store';
import FOFormSidebarBtn from '../../components/fo-form_sidebar-btn';
import { ProfileDetailsList } from '../../interface/fo-profile-interface';
import { profileIndividualSchema } from '../../interface/fo-profile-schema';
import ContactNumberListModal from './contact-details/fo-contact-number-list-modal';
import EmailListModal from './contact-details/fo-email-list-modal';
// import RadioGroupController from './fo-profile-age-group.controller';
import ProfileLogsModal from './fo-profile-logs-modal';
import ProfileTakePhotoModal from './fo-profile-take-photo-modal';
import ProfileVisitHistoryModal from './fo-profile-visit-history-modal';

import { useGetFilesQuery } from '@api/files.api.rtk';
import useConfirmationDialog from '@hooks/useConfirmationDialog';
import { UploadFileParams } from '@interfaces/files.interface';
import { isSaveDisabled } from '@utils/form';
import { titleCase } from 'title-case';
import { getColor } from '../../../../assets/theme';
import useAppContext from '../../../../hooks/useAppContext';
import useDisclosure from '../../../../hooks/useDisclosure';
import useInformationDialog from '../../../../hooks/useInformationDialog';
import useS3Bucket from '../../../../hooks/useS3Bucket';
import FORoomPostingContainer from '../../containers/fo-room_posting.container';
import FOProfileAvatar from './fo-profile-avatar';
import FOProfileGroupDrawer from './fo-profile-group-drawer';
import { PDFGuestDataForm } from './print-guest-data/print-guest-data';
import { CustomMaskFormat } from '@components/form/custom-mask-format';
import { useCurrentModule } from '@hooks/useCurrentModule';
import {
  handleNameKeyDown,
  handleNamePaste,
  handlePaste,
  handlePhoneNumberKeyDown,
} from '@utils/keypress';
import { formatPhoneNumber } from '@utils/format';
import { isValidEmailByProvider } from '@utils/email-format';

export interface IndividualProfile {
  salutation: { salutation: string } | null;
  firstName: string;
  middleName?: string;
  lastName: string;
  nationality: { code: string; caption: string } | null;
  gender?: string | null;
  birthday?: object | null;
  weddingAnniv?: object | null;
  contactNumber: string;
  primaryEmail: string;
  marketSegment?: string | null | '';
  businessSource?: string | null | '';
  sms: boolean;
  email: boolean;
  vipLevel?: { vip_level: string } | null;
  accountOwner?: string | null;
  homeAddress?: string | null;
  cityAddress?: string | null;
  country: { code: string; caption: string } | null;
  company?: string | null;
  department?: string | null;
  designation?: string | null;
  guestRemarks?: string | null;
  ageGroup?: string | null;
  hotel: boolean;
  pos: boolean;
  banquet: boolean;
  athlete: boolean;
  pwd: boolean;
  blacklisted: boolean;
  accountMapping?: string | null;
  vatType?: string | null;
  creditLimit?: number | null;
  creditTerms?: number | null;
  tinNo?: string | null;
  taxCode?: string | null;
  businessStyle?: string | null;
  salesToGov: boolean;
}
export const CustomWidthTooltip = styled(
  ({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ),
)({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 170,
  },
});

//const optionsGender = ['Male', 'Female'];

export const optionsVATType = [
  'VATable',
  'VAT Exempt',
  'Non VAT',
  'Zero Rated',
];

export const optionsVATTypeOptions = [
  {
    label: 'VATable',
    value: 'V',
  },
  {
    label: 'Non VAT',
    value: 'NV',
  },
  {
    label: 'VAT Exempt',
    value: 'E',
  },
  {
    label: 'Zero Rated',
    value: 'Z',
  },
];

const FOProfileIndividualDrawer = ({
  isOpen,
  onClose,
  profileID,
}: {
  isOpen: boolean;
  onClose: () => void;
  profileID?: string | null | undefined;
}) => {
  const selectedPropertyID = useAppSelector(
    (state: RootState) => state.properties.selectedProperty.value,
  );

  const { paperBg } = getColor();

  const { uploadFile, getFile, encryptFile } = useS3Bucket();
  const [fileData, setFileData] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isUploadLoading, setIsUploadLoading] = useState(false);
  const [statusUploadLevel, setStatusUploadLevel] = useState<number>(0);
  const { gray } = getColor();
  const { toast } = useToast();
  const { handleClose } = useMenu();
  const {
    openSecCodeDialog,
    isSecuCodeCancel,
    genpar: { getGenparValue },
  } = useAppContext();
  const { DialogInformation, onShowInfoDialog } = useInformationDialog();
  const currentModule = useCurrentModule();

  const [isModalOpenContact, setIsModalOpenContact] = useState(false);
  const [isModalOpenEmail, setIsModalOpenEmail] = useState(false);
  const [isModalOpenTakePhoto, setIsModalOpenTakePhoto] = useState(false);
  const [isModalOpenVisitHistory, setIsModalOpenVisitHistory] = useState(false);
  const [isModalOpenLogs, setIsModalOpenLogs] = useState(false);

  const [guestBalance, setGuestBalance] = useState<string>('0.00');
  const [chartOfAccountID, setChartOfAccountID] = useState<number>(0);

  // const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('Adult');
  const optionsGender = ['Male', 'Female'];
  const optionsVATType = ['VATable', 'VAT Exempt', 'Non VAT', 'Zero Rated'];
  const [companyList, setCompanyList] = useState<ProfileDetailsList[]>(
    [] as ProfileDetailsList[],
  );
  const [chartOfAccountOptions, setChartOfAccountOptions] = useState<string[]>(
    [],
  );

  const withLockset = getGenparValue('with_lockset') === 'true';

  const [optionsCreditTerms, setOptionsCreditTerms] = useState<number[]>([]);

  const [initialIsHotel, setInitialIsHotel] = useState(false);
  const [initialIsPOS, setInitialIsPOS] = useState(false);
  const [initialIsBanquet, setInitialIsBanquet] = useState(false);

  const [isHotel, setIsHotel] = useState(false);
  const [isPOS, setIsPOS] = useState(false);
  const [isBanquet, setIsBanquet] = useState(false);

  const [optionCompany, setOptionCompanyList] = useState<string[]>([]);
  const [companyId, setCompanyId] = useState<number>(0);
  const [companyProfileId, setCompanyProfileId] = useState<string>('');
  const [fetchFileName, setFetchFileName] = useState<string>('');

  const roomPostingDisclosure = useDisclosure();
  const netlockState = useDisclosure();
  const tracesDisclosure = useDisclosure();
  const deleteProfileDisclosure = useDisclosure();
  const profileCompanyDrawerState = useDisclosure();
  const sidebarBtnLoadingState = useDisclosure();
  const collectionsDisclosure = useDisclosure();

  const disclosures = {
    printDisclosure: useDisclosure(),
  };

  const { printDisclosure } = disclosures;

  type DisclosureKeys = keyof typeof disclosures;

  const handleDisclosureClick = (disclosureKey: DisclosureKeys) => {
    Object.keys(disclosures).forEach((key) => {
      if (key === disclosureKey) {
        disclosures[key as DisclosureKeys].onOpen();
      } else {
        disclosures[key as DisclosureKeys].onClose();
      }
    });
  };
  const printGuestData = useDisclosure();
  const [anchorElPrint, setAnchorElPrint] = useState<HTMLElement | null>(null);
  const handleClickPrintOptions = (
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    setAnchorElPrint(event?.currentTarget as HTMLElement);
  };

  const handleClosePrintOptions = () => {
    setAnchorElPrint(null);
  };

  const [includeCollections, setIncludeCollections] = useState(false);

  const [tabValue, setTabValue] = useState('guest_remarks');
  const [taxCodeId, setTaxCodeId] = useState<number>(0);

  const [fileEncryptedData, setFileEncryptedData] = useState<
    UploadFileParams[]
  >([]);
  const [fileEncryptedString, setFileEncryptedString] = useState<string>('');

  const [isPhotoExisting, setIsPhotoExisting] = useState(false);
  const [triggerFileLoading, setTriggerFileLoading] = useState(false); // used to trigger loading of file

  const bookingOptionList = useAppSelector(
    (state) => state.foBookingOptionList,
  );
  const nationalityList = useAppSelector((state) => state.nationalityList);

  const { property: optionList } = useAppSelector(
    (state) => state.foBookingOptionList,
  );

  const chartOfAccountList = useAppSelector(
    (state) => state.accgChartOfAccount,
  );

  const taxCodeData = useAppSelector((state) => state.accgTaxCode);

  const customersList = useAppSelector((state) => state.accgCustomers);
  const [taxCodeList, setTaxCodeList] = useState<string[]>([]);

  const [createProfile, { isLoading: isLoadingCreate }] =
    usePostProfileMutation();
  const [updateProfile, { isLoading: isLoadingUpdate }] =
    useUpdateProfileMutation();

  const { data: profileListCompany } = useGetProfileQuery(
    `?filter[accountType]=Company`,
    {
      skip: !isOpen,
    },
  );

  const {
    data: profileData,
    isFetching: isFetchingDetails,
    isLoading: isLoadingDetails,
  } = useGetProfileDetailsQuery(`${profileID}`, {
    skip: !isOpen || !profileID,
  });

  const { data: uploadedData, isFetching: isFileListLoading } =
    useGetFilesQuery(profileID!, {
      skip: !isOpen || !profileID,
    });

  const [deleteNetLock] = useDeleteNetLockMutation();

  useEffect(() => {
    if (!isOpen) return;

    if (taxCodeData?.dataResult) {
      const taxCodeList = taxCodeData.dataResult.map(
        (item: { code: string }) => item.code,
      );
      setTaxCodeList(taxCodeList);

      if (!profileID && taxCodeList.length) {
        setValue('taxCode', taxCodeList[0]);
        handleTaxChange(taxCodeList[0]);
      }
    } else {
      toast({
        message: 'Kindly configure Alphanumeric Tax Code',
        severity: 'error',
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (chartOfAccountList && chartOfAccountList.length > 0) {
      const accountDetailedList = chartOfAccountList.map((account) => ({
        ...account,
      }));

      const mappedOptionsReceivable: string[] = accountDetailedList
        .map((account) => account.accountName)
        .sort((a, b) => a.localeCompare(b));

      setChartOfAccountOptions(mappedOptionsReceivable);
    }
  }, [isOpen]);

  const handleTaxChange = (code: string) => {
    try {
      const selectedTaxCode = taxCodeData?.dataResult.find(
        (taxCode: { code: string; id: number }) => taxCode.code === code,
      );

      setTaxCodeId(selectedTaxCode?.id || 0);
    } catch (error) {
      console.error('Error selecting tax code:', error);
    }
  };

  const handleChartOfAccountChange = (selectedChartOfAccount: string) => {
    try {
      const findChartOfAccount = chartOfAccountList.find(
        (account) => account.accountName === selectedChartOfAccount,
      );

      if (findChartOfAccount) {
        setChartOfAccountID(findChartOfAccount.id);
      } else {
        console.warn(
          `Chart of Account not found for: ${selectedChartOfAccount}`,
        );
      }
    } catch (error) {
      console.error(
        `Error selecting Chart of Account: ${selectedChartOfAccount}`,
        error,
      );
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (!profileListCompany?.data?.profiles?.length) return;
    const profileDetailedList = profileListCompany.data.profiles.map(
      (profileWrapper) => profileWrapper.profiles,
    );

    setCompanyList(profileDetailedList);

    const mappedOptionsCompany: string[] = profileDetailedList.map(
      (profile: ProfileDetailsList) => `${profile.caption}`,
    );
    setOptionCompanyList(mappedOptionsCompany);

    if (!profileID && mappedOptionsCompany.length) {
      setValue('company', mappedOptionsCompany[0]);
    }

    // credit terms options
    const mappedOptionsCreditTerms: number[] = [
      ...new Set(
        customersList.map((customer) => customer.profiles.creditTerms),
      ),
    ];

    setOptionsCreditTerms(mappedOptionsCreditTerms);
  }, [isOpen, profileListCompany]);

  const handleCompanyChange = (selectedCompany: string) => {
    if (!selectedCompany) {
      setCompanyId(0);
      setCompanyProfileId('');
      setValue('department', '');
      setValue('designation', '');
    }
    try {
      const findCompany = companyList.find(
        (profiles) => profiles.caption === selectedCompany,
      );

      if (findCompany) {
        setCompanyId(findCompany?.id);
        setCompanyProfileId(findCompany?.profileID);
      } else {
        console.warn(`Company not found for: ${selectedCompany}`);
      }
    } catch (error) {
      console.error(`Error selecting Company: ${selectedCompany}`, error);
    }
  };

  useEffect(() => {
    setStatusUploadLevel(0);
    if (
      !profileID ||
      !isOpen ||
      isLoadingDetails ||
      isFetchingDetails ||
      !profileData?.data.profiles
    )
      return;

    try {
      const profiles = profileData.data.profiles;

      for (const profileWrapper of profiles) {
        const profile = profileWrapper.profile;
        const [lastName, firstName, middleName] = profile.caption.split(', ');

        // PRIMARY DETAILS
        setValue(
          'salutation',
          bookingOptionList?.property?.salutations?.find(
            (item) => item.salutation == profile.salutation,
          ) ?? null,
        );
        setValue('firstName', firstName);
        setValue('middleName', middleName);
        setValue('lastName', lastName);
        setValue(
          'nationality',
          nationalityList?.find((item) => item.code == profile.nationality) ??
            null,
        );
        setValue('gender', profile.gender ?? '');
        profile?.dateBDay && setValue('birthday', moment(profile.dateBDay));
        profile?.dateWeddingAnniv &&
          setValue('weddingAnniv', moment(profile.dateWeddingAnniv));

        // CONTACT DETAILS
        setValue('contactNumber', profile.contact);
        setValue('primaryEmail', profile.email);

        // MARKETING DETAILS
        setValue('marketSegment', profile.msegment);
        setValue('businessSource', profile.bsource);
        setValue('sms', profile.isSMS);
        setValue('email', profile.isEmail);
        profile?.vip !== '0' &&
          setValue(
            'vipLevel',
            bookingOptionList?.property?.VIPLevels?.find((item) =>
              item?.vip_level.includes(profile?.vip),
            ),
          );
        setValue('accountOwner', profile.owner);

        // ADDRESS
        setValue('homeAddress', profile.address);
        setValue('cityAddress', profile.province);
        setValue('country', {
          code: profile.nationality,
          caption: profile.countryName,
        });

        // COMPANY DETAILS
        setCompanyId(profile.companyID);
        setCompanyProfileId(profile.companyAccountID);
        setValue('company', profile.companyName);
        setValue('department', profile.companyDepartment);
        setValue('designation', profile.companyDesignation);

        setValue('guestRemarks', profile.remarks);

        // AGE GROUP
        // setSelectedAgeGroup(profile.ageGroup);
        setValue('ageGroup', profile.ageGroup);

        // ALLOWED ACCOUNTS
        setValue('hotel', profile.isCLHotel);
        setValue('pos', profile.isCLPOS);
        setValue('banquet', profile.isCLBanquet);

        setInitialIsHotel(profile.isCLHotel);
        setInitialIsPOS(profile.isCLPOS);
        setInitialIsBanquet(profile.isCLBanquet);

        setIsHotel(profile.isCLHotel);
        setIsPOS(profile.isCLPOS);
        setIsBanquet(profile.isCLBanquet);

        // OTHER
        setValue('athlete', profile.isAthlete);
        setValue('pwd', profile.isPWD);
        setValue('blacklisted', profile.isBlackListed);
        setChartOfAccountID(profile.coaID);
        setValue('accountMapping', profile.accountMapping);
        setValue('vatType', profile.vatType);
        setValue('creditLimit', profile.creditLimit);
        setValue('creditTerms', profile.creditTerms);

        setValue('tinNo', profile.tinNo);
        setValue('taxCode', profile.taxCode);
        setValue('businessStyle', profile.taxBusStyle);
        setValue('salesToGov', profile.isSalesToGov);

        setGuestBalance(profile.balance);

        setFetchFileName(profile.fileName);

        if (profile.netLock === 1) {
          toast({
            severity: 'warning',
            message: `${
              profile.lockByUserShortName || 'Someone'
            } is editing this Profile.`,
          });
          netlockState.onOpen();
        }
      }
    } catch (error) {
      console.error('Error fetching profile details:', error);
    }
  }, [profileData, isOpen, isFetchingDetails, isLoadingDetails]);

  useEffect(() => {
    (async () => {
      if (isFileListLoading) return;
      setIsLoading(true);

      const fileID = uploadedData?.[0]?.fileID; // get the first file ID always

      if (!fileID) {
        if (fetchFileName) {
          setIsPhotoExisting(false);
          setFileUrl('');
        }
        setIsLoading(false);
        return;
      }

      const imageUrl = await getFile(fileID);
      if (imageUrl) {
        setFileUrl(imageUrl);
        setIsPhotoExisting(true);
      } else {
        setFileUrl('');
        setIsPhotoExisting(false);
      }

      setIsLoading(false);
    })();
  }, [uploadedData]);

  useEffect(() => {
    if (isSecuCodeCancel) {
      if (!profileID) {
        setValue('hotel', false);
        setValue('pos', false);
        setValue('banquet', false);

        setIsHotel(false);
        setIsPOS(false);
        setIsBanquet(false);
      } else {
        setValue('hotel', initialIsHotel);
        setValue('pos', initialIsPOS);
        setValue('banquet', initialIsBanquet);

        setIsHotel(initialIsHotel);
        setIsPOS(initialIsPOS);
        setIsBanquet(initialIsPOS);

        setInitialIsHotel(initialIsHotel);
        setInitialIsPOS(initialIsPOS);
        setInitialIsBanquet(initialIsBanquet);
      }
    }
  }, [isSecuCodeCancel]);

  useEffect(() => {
    // set encrypted file data using file and encrypted data
    if (fileEncryptedString && fileData) {
      setFileEncryptedData([
        {
          fileData,
          fileEncryptedData: fileEncryptedString,
        },
      ]);
    }
  }, [fileEncryptedString]);

  useEffect(() => {
    (async () => {
      if (!fileData) return;
      await encryptFile(fileData, setFileEncryptedString, (level) =>
        setStatusUploadLevel(level),
      );
    })();
  }, [fileData]);

  useEffect(() => {
    if (!triggerFileLoading) {
      return;
    }

    // for upload loader
    if (statusUploadLevel === 100) {
      setIsUploadLoading(false);
      setTriggerFileLoading(false);
      setStatusUploadLevel(0);
    } else {
      setIsUploadLoading(true);
    }
  }, [statusUploadLevel, triggerFileLoading]);

  useEffect(() => {
    if (!isOpen || profileID) return; // Used only for Create Profile

    const firstMarketSegment =
      optionList?.marketSegment?.[0]?.market_segment.ms_code;
    if (!firstMarketSegment) return;

    setValue('marketSegment', firstMarketSegment);

    const matchedSegment = optionList?.marketSegment?.find(
      (item) => item.market_segment.ms_code === firstMarketSegment,
    );

    const firstBusinessSource =
      matchedSegment?.market_segment.ms_options[0]?.business_source?.[0]
        ?.bsource_code;

    if (firstBusinessSource) {
      setValue('businessSource', firstBusinessSource);
    }
  }, [isOpen, optionList]);

  const handleDrawerClose = async () => {
    reset();
    setFileUrl('');
    setCompanyId(0);
    setCompanyProfileId('');
    netlockState.onClose();
    onClose();
    profileID &&
      deleteNetLock(`${profileID}/Profile`)
        .unwrap()
        .catch(() =>
          toast({
            severity: 'error',
            message: 'Error deleting of net lock on database.',
          }),
        );
  };

  const handleOpenModalContact = () => {
    setIsModalOpenContact(true);
    handleClose();
  };

  const handleOpenModalEmail = () => {
    setIsModalOpenEmail(true);
    handleClose();
  };

  const handleOpenModalVisitHistory = () => {
    setIsModalOpenVisitHistory(true);
    handleClose();
  };

  const handleOpenModalLogs = () => {
    setIsModalOpenLogs(true);
    handleClose();
  };
  const { DialogComponentV2, DialogComponentV2: DeleteProfileDialog } =
    useConfirmationDialog();

  const handleCloseModalContact = () => setIsModalOpenContact(false);
  const handleCloseModalEmail = () => setIsModalOpenEmail(false);
  const handleCloseModalTakePhoto = () => setIsModalOpenTakePhoto(false);
  const handleCloseModalVisitHistory = () => setIsModalOpenVisitHistory(false);
  const handleCloseModalLogs = () => setIsModalOpenLogs(false);

  const handleIndividualProfile: SubmitHandler<IndividualProfile> = async (
    data,
  ) => {
    if (!profileID) {
      if (isHotel || isPOS || isBanquet) {
        handleShowDialog(data, 'create');
      } else {
        await processProfile(data, 'create');
      }
    } else {
      if (
        isHotel !== initialIsHotel ||
        isPOS !== initialIsPOS ||
        isBanquet !== initialIsBanquet
      ) {
        handleShowDialog(data, 'update');
      } else {
        await processProfile(data, 'update');
      }
    }
  };

  const handleShowDialog = async (
    data: IndividualProfile,
    action: 'create' | 'update',
  ) => {
    onShowInfoDialog({
      title: 'Restricted Feature',
      desc: `A security code is required to enable or disable the City Ledger account feature for this customer account.
            
Please enter a valid security code to continue.`,
      onOk: () => {
        openSecCodeDialog(async (result) => {
          if (result?.data.isValid) {
            await processProfile(data, action);
          } else {
            return;
          }
        }, 'config_cl_accounts');
      },
    });
  };

  const processProfile = async (
    data: IndividualProfile,
    action: 'create' | 'update',
  ) => {
    const {
      salutation,
      lastName,
      firstName,
      middleName,
      nationality,
      gender,
      birthday,
      weddingAnniv,
      contactNumber,
      primaryEmail,
      marketSegment,
      businessSource,
      sms,
      email,
      vipLevel,
      accountOwner,
      homeAddress,
      cityAddress,
      country,
      department,
      designation,
      guestRemarks,
      ageGroup,
      hotel,
      pos,
      banquet,
      athlete,
      pwd,
      blacklisted,
      vatType,
      creditLimit,
      creditTerms,
      tinNo,
      businessStyle,
      salesToGov,
    } = data;

    const formattedDateOfBirth = birthday
      ? moment(birthday).tz('Asia/Manila').format('YYYY-MM-DD')
      : '';
    const formattedDateOfWedding = weddingAnniv
      ? moment(weddingAnniv).tz('Asia/Manila').format('YYYY-MM-DD')
      : '';

    const profileType = 'Individual';
    const titleCaseFullName = middleName
      ? titleCase(`${lastName}, ${firstName}, ${middleName}`)
      : titleCase(`${lastName}, ${firstName}`);
    const salutationType = String(salutation?.salutation || '');
    const profileNationality = String(nationality?.code || '');
    const userGender = gender ?? '';
    const vip = String(vipLevel?.vip_level?.split(' ')?.[1] || 0);
    const profileBusinessSource = businessSource ?? '';
    const owner = accountOwner ?? '';
    const coaId = chartOfAccountID ?? 0;
    const address = homeAddress ?? '';
    const city = cityAddress ?? '';
    const vat = vatType ?? '';
    const companyID = companyId;
    const profileCountry = String(country?.code || '');

    const signature = SHA512(
      selectedPropertyID +
        (action === 'update' ? profileID : '') +
        profileType +
        fileName +
        titleCaseFullName +
        contactNumber +
        primaryEmail +
        marketSegment +
        profileBusinessSource +
        owner +
        address +
        city +
        String(profileCountry) +
        String(vip) +
        creditLimit +
        creditTerms +
        vat +
        coaId +
        guestRemarks +
        +!!sms +
        +!!email +
        +!!blacklisted +
        +!!hotel +
        +!!pos +
        +!!banquet +
        String(salutationType) +
        userGender +
        String(profileNationality) +
        ageGroup +
        companyID +
        department +
        designation +
        +!!athlete +
        +!!pwd +
        formattedDateOfBirth +
        formattedDateOfWedding +
        tinNo +
        taxCodeId +
        businessStyle +
        +!!salesToGov,
    ).toString();

    const individual = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            profiles: [
              {
                profile: {
                  ...(action === 'update' && { profileID: profileID }),
                  type: profileType,
                  salutation: String(salutationType),
                  fileName: fileName,
                  caption: titleCaseFullName,
                  nationality: String(profileNationality),
                  gender: userGender,
                  dateOfBirth: formattedDateOfBirth,
                  dateOfWedding: formattedDateOfWedding,
                  contact: contactNumber,
                  email: primaryEmail,
                  marketSegment: marketSegment,
                  businessSource: businessSource,
                  isSMS: sms,
                  isEmail: email,
                  vip: String(vip),
                  owner: owner,
                  address: address,
                  city: city,
                  country: String(profileCountry),
                  companyID: companyID,
                  companyDept: department,
                  companyDesignation: designation,
                  remarks: guestRemarks,
                  ageGroup: ageGroup,
                  isHotel: hotel,
                  isPOS: pos,
                  isBanquet: banquet,
                  isAthlete: athlete,
                  isPWD: pwd,
                  isBlacklisted: blacklisted,
                  coaID: coaId,
                  vatType: vat,
                  creditLimit: creditLimit,
                  creditTerms: creditTerms,
                  tinNo: tinNo,
                  taxCodeId: taxCodeId,
                  businessStyle: businessStyle,
                  isSalesToGov: salesToGov,
                  signature: signature,
                },
              },
            ],
          },
        },
      ],
    };

    try {
      if (action === 'create') {
        await createProfile(individual)
          .unwrap()
          .then((profileReferenceId) => {
            if (
              fileData &&
              profileReferenceId &&
              fileEncryptedData.length > 0
            ) {
              uploadFile(
                profileReferenceId,
                fileEncryptedData,
                'Profile Photo',
                'Profiles',
                true,
              );
            }
            toast({
              severity: 'success',
              message: 'Profile added successfully',
            });
            reset();
            handleDrawerClose();
          })
          .catch((error) => {
            toast({
              severity: 'error',
              message: error,
            });
          });
      } else {
        await updateProfile(individual)
          .unwrap()
          .then(() => {
            if (
              fileData &&
              profileID &&
              fileEncryptedData.length > 0 &&
              !isPhotoExisting
            ) {
              uploadFile(
                profileID,
                fileEncryptedData,
                'Profile Photo',
                'Profiles',
                true,
              );
            }
            toast({
              severity: 'success',
              message: 'Updated successfully',
            });
            reset();
            handleDrawerClose();
          })
          .catch((error) => {
            toast({
              severity: 'error',
              message: error,
            });
          });
      }
    } catch (err) {
      toast({
        severity: 'error',
        message: err,
      });
    }
  };

  const handleTabChange = (_e: React.SyntheticEvent, newValue: string) => {
    if (typeof newValue !== 'string') return;
    setTabValue(newValue);
  };

  const methods = useForm<IndividualProfile>({
    defaultValues: {
      salutation: { salutation: 'Mr.' },
      lastName: '',
      firstName: '',
      middleName: '',
      nationality: { code: 'PHL', caption: 'Philippines' },
      gender: 'Male',
      birthday: null,
      weddingAnniv: null,
      contactNumber: '',
      primaryEmail: '',
      marketSegment: '',
      businessSource: '',
      sms: true,
      email: true,
      vipLevel: null,
      accountOwner: null,
      homeAddress: '',
      cityAddress: null,
      country: { code: 'PHL', caption: 'Philippines' },
      company: '',
      department: '',
      designation: '',
      guestRemarks: '',
      ageGroup: 'Adult',
      hotel: false,
      pos: false,
      athlete: false,
      pwd: false,
      banquet: false,
      blacklisted: false,
      accountMapping: '',
      vatType: 'VATable',
      creditLimit: 0,
      creditTerms: 90,
      tinNo: '',
      taxCode: '',
      businessStyle: '',
      salesToGov: false,
    },
    resolver: yupResolver<IndividualProfile>(
      profileIndividualSchema(withLockset),
    ),
    mode: 'onSubmit',
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = methods;

  const [deleteProfile] = useDeleteProfileMutation();
  const handleDeleteProfile = async () => {
    openSecCodeDialog(async (result) => {
      if (!result?.data.isValid) {
        return;
      }
      await deleteProfile(profileID)
        .unwrap()
        .then(() => {
          handleDrawerClose();
          toast({
            severity: 'success',
            message: 'Profile deleted successfully.',
          });
        })
        .catch((err) => {
          toast({
            severity: 'error',
            message: err,
          });
        });
    }, 'del_guest_prof');
  };

  const { data: profileVisitHistory } = useGetProfileVisitHistoryQuery(
    `${profileID}`,
    {
      skip: !isOpen,
      refetchOnMountOrArgChange: true,
    },
  );

  const hasVisits = (profileVisitHistory?.data?.visits?.length ?? 0) > 0;

  return (
    <>
      <FormProvider {...methods}>
        <DrawerWrapper
          {...{
            id: 'individual-profile-drawer',
            open: isOpen,
            onClose: handleDrawerClose,
            withHeader: {
              title: (
                <Stack direction='row' flexWrap='wrap' gap={2}>
                  <ContainerTitle
                    title='Profile'
                    func={() => {}}
                    titleStyle={{ fontSize: '20px!important' }}
                  />
                  <Stack
                    direction='row'
                    gap={2}
                    sx={{
                      marginLeft: 'auto',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Typography>
                      Outstanding Balance:
                      <Typography
                        component='span'
                        sx={{
                          fontWeight: 550,
                          marginLeft: 1,
                          fontSize: '20px',
                          color: '#2B3493',
                        }}
                      >
                        {guestBalance}
                      </Typography>
                    </Typography>
                  </Stack>
                </Stack>
              ),
              learnMore: () => {},
            },
            isLoading: isLoadingCreate || isLoadingUpdate || isFetchingDetails,
          }}
        >
          {/* MAIN */}
          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            sx={{
              opacity: netlockState.isOpen ? 0.5 : 'unset',
              pointerEvents: netlockState.isOpen ? 'none' : 'unset',
              width: { xs: '100%', lg: '1100px' },
              maxHeight: '100dvh',
              flex: 1,
              overflowY: { xs: 'auto', md: 'hidden' },
              ':hover': {
                overflowY: 'auto',
              },
            }}
          >
            {/* CONTENT */}
            <Stack
              flex={1}
              sx={{
                width: '100%',
              }}
            >
              {/* FORM DETAILS */}
              <Stack
                component='form'
                id='individual-profile'
                onSubmit={handleSubmit(handleIndividualProfile)}
                sx={{
                  flex: 1,
                  height: '100%',
                  padding: '0 16px',
                  overflowY: 'hidden',
                  ':hover': {
                    overflowY: { xs: 'hidden', sm: 'auto' },
                  },
                }}
              >
                <Grid2
                  container
                  spacing={3}
                  sx={{
                    py: 2,
                  }}
                >
                  {/* UPLOAD IMAGE */}
                  <Grid2 {...{ xs: 12, md: 4 }}>
                    <Stack gap={2}>
                      {/* AVATAR */}
                      <FOProfileAvatar
                        isLoading={isLoading || isUploadLoading}
                        fileUrl={fileUrl}
                        handleUploadPhoto={(fileData, fileUrl, fileName) => {
                          setFileData(fileData);
                          setFileUrl(fileUrl);
                          setFileName(fileName);
                          setIsPhotoExisting(false);
                          setTriggerFileLoading(true);
                        }}
                        handleOpenModalTakePhoto={() => {
                          setIsModalOpenTakePhoto(true);
                          handleClose();
                        }}
                      />

                      {/* AGE GROUP */}
                      {/* <Stack>
                        <GroupTitle
                          title='Age Group'
                          wrapperStyle={{ marginBottom: 1 }}
                        />
                        <Stack pl={1}>
                          <RadioGroupController
                            control={control}
                            name='ageGroup'
                            defaultValue={selectedAgeGroup}
                          />
                        </Stack>
                      </Stack> */}

                      {/* ALLOWED ACCOUNTS */}
                      <Stack>
                        <GroupTitle
                          title='Allowed Accounts'
                          wrapperStyle={{ marginBottom: 1 }}
                        />
                        <Stack
                          direction='row'
                          alignItems='center'
                          sx={{
                            p: 1,
                            borderBottom: '1px dashed lightgray',
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>Hotel</Typography>
                          <ThemeProvider theme={DefaultSwitch}>
                            <FormControlLabel
                              control={
                                <Controller
                                  name='hotel'
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      {...field}
                                      checked={field.value}
                                      onChange={(e) => {
                                        field.onChange(e.target.checked);
                                        setIsHotel(e.target.checked);
                                      }}
                                    />
                                  )}
                                />
                              }
                              label=''
                              labelPlacement='start'
                            />
                          </ThemeProvider>
                        </Stack>
                        <Stack
                          direction='row'
                          alignItems='center'
                          sx={{
                            p: 1,
                            borderBottom: '1px dashed lightgray',
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>POS</Typography>
                          <ThemeProvider theme={DefaultSwitch}>
                            <FormControlLabel
                              control={
                                <Controller
                                  name='pos'
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      {...field}
                                      checked={field.value}
                                      onChange={(e) => {
                                        field.onChange(e.target.checked);
                                        setIsPOS(e.target.checked);
                                      }}
                                    />
                                  )}
                                />
                              }
                              label=''
                              labelPlacement='start'
                            />
                          </ThemeProvider>
                        </Stack>
                        <Stack
                          direction='row'
                          alignItems='center'
                          sx={{
                            p: 1,
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>Banquet</Typography>
                          <ThemeProvider theme={DefaultSwitch}>
                            <FormControlLabel
                              control={
                                <Controller
                                  name='banquet'
                                  control={control}
                                  render={({ field }) => (
                                    <Switch
                                      {...field}
                                      checked={field.value}
                                      onChange={(e) => {
                                        field.onChange(e.target.checked);
                                        setIsBanquet(e.target.checked);
                                      }}
                                    />
                                  )}
                                />
                              }
                              label=''
                              labelPlacement='start'
                            />
                          </ThemeProvider>
                        </Stack>
                      </Stack>

                      {/* OTHER */}
                      {/* <Stack>
                        <GroupTitle
                          title='Other'
                          wrapperStyle={{ marginBottom: 1 }}
                        />
                        <Stack
                          direction='row'
                          alignItems='center'
                          sx={{
                            p: 1,
                            borderBottom: '1px dashed lightgray',
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>Athlete</Typography>
                          <ThemeProvider theme={DefaultSwitch}>
                            <FormControlLabel
                              control={
                                <Controller
                                  name='athlete'
                                  control={control}
                                  render={({ field }) => (
                                    <Switch {...field} checked={field.value} />
                                  )}
                                />
                              }
                              label=''
                              labelPlacement='start'
                            />
                          </ThemeProvider>
                        </Stack>
                        <Stack
                          direction='row'
                          alignItems='center'
                          sx={{
                            p: 1,
                            borderBottom: '1px dashed lightgray',
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>PWD</Typography>
                          <ThemeProvider theme={DefaultSwitch}>
                            <FormControlLabel
                              control={
                                <Controller
                                  name='pwd'
                                  control={control}
                                  render={({ field }) => (
                                    <Switch {...field} checked={field.value} />
                                  )}
                                />
                              }
                              label=''
                              labelPlacement='start'
                            />
                          </ThemeProvider>
                        </Stack>
                        <Stack
                          direction='row'
                          alignItems='center'
                          sx={{
                            p: 1,
                          }}
                        >
                          <Typography sx={{ flexGrow: 1 }}>
                            Blacklisted
                          </Typography>
                          <ThemeProvider theme={DefaultSwitch}>
                            <FormControlLabel
                              control={
                                <Controller
                                  name='blacklisted'
                                  control={control}
                                  render={({ field }) => (
                                    <Switch {...field} checked={field.value} />
                                  )}
                                />
                              }
                              label=''
                              labelPlacement='start'
                            />
                          </ThemeProvider>
                        </Stack>
                      </Stack> */}

                      <Stack direction='row' alignItems='center'>
                        <AutoCompleteController
                          control={control}
                          errors={errors}
                          label='Account Mapping'
                          name='accountMapping'
                          options={chartOfAccountOptions || []}
                          onChange={handleChartOfAccountChange}
                          borderErrorOnly
                          style={{ margin: 0 }}
                        />
                      </Stack>

                      <Stack direction='row' spacing={2}>
                        <AutoCompleteController
                          control={control}
                          errors={errors}
                          label='Tax Type'
                          name='vatType'
                          options={optionsVATType || []}
                          borderErrorOnly
                          style={{ margin: 0 }}
                        />
                      </Stack>

                      <Stack direction='row' spacing={2}>
                        <InputFieldsController
                          control={control}
                          errors={errors}
                          label='Credit Limit'
                          name='creditLimit'
                          inputProps={{
                            style: { textAlign: 'right' },
                            onFocus: () => true,
                          }}
                          InputProps={{
                            inputComponent: (
                              props: CustomNumberFormatProps,
                            ) => (
                              <CustomNumberFormat
                                {...props}
                                allowNegative={false}
                                isNegative={false}
                              />
                            ),
                          }}
                          autoComplete='on'
                          borderErrorOnly
                          wrapperStyle={{
                            marginBottom: 0,
                          }}
                        />
                        <AutoCompleteController
                          control={control}
                          errors={errors}
                          label='Credit Terms'
                          name='creditTerms'
                          options={optionsCreditTerms || []}
                          borderErrorOnly
                          style={{ margin: 0 }}
                          textFieldProps={{
                            inputProps: {
                              maxLength: 3,
                            },
                          }}
                          freeSolo
                        />
                      </Stack>
                    </Stack>
                  </Grid2>

                  {/* PROFILE DETAILS */}
                  <Grid2 {...{ xs: 12, md: 8 }}>
                    <Stack gap={2}>
                      {/* PRIMARY DETAILS */}
                      <Box>
                        <Grid2 {...{ xs: 12 }} container gap={1}>
                          <Grid2 {...{ xs: 12 }}>
                            <GroupTitle title='Primary Details' />
                          </Grid2>
                        </Grid2>
                        <Stack gap={2}>
                          <Grid2 container spacing={2}>
                            <Grid2 {...{ sm: 12, md: 4.5 }}>
                              <InputFieldsController
                                label='Last Name *'
                                name='lastName'
                                control={control}
                                errors={errors}
                                InputProps={{
                                  autoComplete: 'last-name',
                                }}
                                borderErrorOnly
                                wrapperStyle={{
                                  marginBottom: 0,
                                }}
                                onKeyDown={handleNameKeyDown}
                                onPaste={handleNamePaste}
                                trigger={trigger}
                                capitalizeText
                              />
                            </Grid2>
                            <Grid2 {...{ sm: 12, md: 4.5 }}>
                              <InputFieldsController
                                label='First Name'
                                name='firstName'
                                control={control}
                                errors={errors}
                                InputProps={{
                                  autoComplete: 'first-name',
                                }}
                                borderErrorOnly
                                wrapperStyle={{
                                  marginBottom: 0,
                                }}
                                onKeyDown={handleNameKeyDown}
                                onPaste={handleNamePaste}
                                trigger={trigger}
                                capitalizeText
                              />
                            </Grid2>
                            {/* <Grid2 {...{ xs: 12, md: 10 / 3 }}>
                              <InputFieldsController
                                label='Middle Name'
                                name='middleName'
                                control={control}
                                errors={errors}
                                InputProps={{
                                  autoComplete: 'middle-name',
                                }}
                                borderErrorOnly
                                wrapperStyle={{
                                  marginBottom: 0,
                                }}
                              />
                            </Grid2> */}
                            <Grid2 {...{ sm: 12, md: 3 }}>
                              <AutoCompleteController
                                control={control}
                                errors={errors}
                                label='Salutation'
                                name='salutation'
                                options={
                                  bookingOptionList?.property?.salutations || []
                                }
                                getOptionLabel={(option: any) =>
                                  option.salutation
                                }
                                borderErrorOnly
                                style={{ margin: 0 }}
                                trigger={trigger}
                              />
                            </Grid2>
                          </Grid2>
                          <Grid2 container spacing={2}>
                            <Grid2 {...{ xs: 12, md: 4 }}>
                              <AutoCompleteController
                                control={control}
                                errors={errors}
                                label='Nationality *'
                                name='nationality'
                                options={nationalityList || []}
                                getOptionLabel={(option: any) => option.code}
                                borderErrorOnly
                                style={{ margin: 0 }}
                              />
                            </Grid2>
                            <Grid2 {...{ xs: 12, md: 4 }}>
                              <AutoCompleteController
                                control={control}
                                errors={errors}
                                label='Gender'
                                name='gender'
                                options={optionsGender || []}
                                borderErrorOnly
                                style={{ margin: 0 }}
                              />
                            </Grid2>
                            <Grid2
                              {...{ xs: 12, md: 4 }}
                              sx={{ pb: { xs: '10px', md: 'none' } }}
                            >
                              <DatePickerController
                                label='Birthday'
                                name='birthday'
                                format='MMMM DD, YYYY'
                                control={control}
                                disablePast={false}
                              />
                            </Grid2>
                            {/* <Grid2
                              {...{ xs: 12, md: 3 }}
                              sx={{ pb: { xs: '10px', md: 'none' } }}
                            >
                              <DatePickerController
                                label='Wedding Anniv'
                                name='weddingAnniv'
                                format='MMMM DD, YYYY'
                                control={control}
                                style={{ width: '100%' }}
                                disablePast={false}
                              />
                            </Grid2> */}
                          </Grid2>
                        </Stack>
                      </Box>

                      {/* CONTACT DETAILS */}
                      <Box>
                        <Grid2 {...{ xs: 12 }} container gap={1}>
                          <Grid2 {...{ xs: 12 }}>
                            <GroupTitle title='Contact Details' />
                          </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                          <Grid2 {...{ xs: 12, md: 6 }}>
                            <InputFieldsController
                              label='Contact Number *'
                              name='contactNumber'
                              control={control}
                              errors={errors}
                              inputStyle={{
                                '.MuiInputBase-root': { width: '100%' },
                              }}
                              InputProps={{
                                autoComplete: 'new-contact',
                                startAdornment: (
                                  <InputAdornment position='start'>
                                    <PhoneInTalkOutlined />
                                  </InputAdornment>
                                ),
                                endAdornment: profileID && (
                                  <InputAdornment position='end'>
                                    <IconButton
                                      edge='end'
                                      onClick={handleOpenModalContact}
                                      style={{
                                        padding: '6px',
                                        backgroundColor: '#2B3493',
                                        borderRadius: '4px',
                                        color: '#ffffff',
                                      }}
                                    >
                                      <Search />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              borderErrorOnly
                              wrapperStyle={{
                                marginBottom: 0,
                              }}
                              inputProps={{ maxLength: 13 }}
                              onPaste={handlePaste}
                              onChange={(e) => {
                                setValue(
                                  'contactNumber',
                                  formatPhoneNumber(e.target.value),
                                );
                              }}
                              onKeyDown={handlePhoneNumberKeyDown}
                              trigger={trigger}
                            />
                          </Grid2>
                          <Grid2 {...{ xs: 12, md: 6 }}>
                            <InputFieldsController
                              label={`Email ${withLockset ? '*' : ''}`}
                              name='primaryEmail'
                              control={control}
                              errors={errors}
                              inputStyle={{
                                '.MuiInputBase-root': { width: '100%' },
                              }}
                              InputProps={{
                                autoComplete: 'new-email',
                                startAdornment: (
                                  <InputAdornment position='start'>
                                    <EmailOutlined />
                                  </InputAdornment>
                                ),
                                endAdornment: profileID && (
                                  <InputAdornment position='end'>
                                    <IconButton
                                      edge='end'
                                      onClick={handleOpenModalEmail}
                                      style={{
                                        padding: '6px',
                                        backgroundColor: '#2B3493',
                                        borderRadius: '4px',
                                        color: '#ffffff',
                                      }}
                                    >
                                      <Search />
                                    </IconButton>
                                  </InputAdornment>
                                ),
                              }}
                              borderErrorOnly
                              wrapperStyle={{
                                marginBottom: 0,
                              }}
                              validateTrigger
                              trigger={trigger}
                              onBlur={(e) => {
                                const value = e.target.value;
                                if (value && !isValidEmailByProvider(value)) {
                                  toast({
                                    severity: 'warning',
                                    message:
                                      'Please provide a valid email address.',
                                  });
                                }
                              }}
                            />
                          </Grid2>
                        </Grid2>
                      </Box>

                      {/* MARKETING DETAILS */}
                      <Box>
                        <Grid2 {...{ xs: 12 }} container gap={1}>
                          <Grid2 {...{ xs: 12 }}>
                            <GroupTitle title='Marketing Details' />
                          </Grid2>
                        </Grid2>
                        <Stack gap={2}>
                          <Grid2 container spacing={2}>
                            <Grid2 {...{ xs: 12, md: 6 }}>
                              <AutoCompleteController
                                control={control}
                                errors={errors}
                                label='Market Segment'
                                name='marketSegment'
                                options={
                                  optionList?.marketSegment?.map(
                                    (item) => item.market_segment.ms_code,
                                  ) || []
                                }
                                style={{ margin: 0, width: '100%' }}
                                textFieldProps={{
                                  InputProps: {
                                    startAdornment: (
                                      <InputAdornment
                                        position='start'
                                        sx={{
                                          width: '24px',
                                          pl: '7px',
                                          pr: '14px',
                                        }}
                                      >
                                        <AutoGraph />
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                                borderErrorOnly
                                onChange={() => setValue('businessSource', '')}
                              />
                            </Grid2>
                            <Grid2 {...{ xs: 12, md: 6 }}>
                              <AutoCompleteController
                                control={control}
                                errors={errors}
                                label='Business Source'
                                name='businessSource'
                                options={
                                  optionList?.marketSegment
                                    ?.find(
                                      (item) =>
                                        item.market_segment.ms_code ==
                                        watch('marketSegment'),
                                    )
                                    ?.market_segment.ms_options[0]?.business_source?.map(
                                      (item) => item.bsource_code,
                                    ) || []
                                }
                                style={{ margin: 0, width: '100%' }}
                                textFieldProps={{
                                  InputProps: {
                                    startAdornment: (
                                      <InputAdornment
                                        position='start'
                                        sx={{
                                          width: '24px',
                                          pl: '7px',
                                          pr: '14px',
                                        }}
                                      >
                                        <Business />
                                      </InputAdornment>
                                    ),
                                  },
                                }}
                                borderErrorOnly
                                disabled={!watch('marketSegment')}
                                onChange={(value: string) =>
                                  setValue('businessSource', value ?? '')
                                }
                              />
                            </Grid2>
                          </Grid2>
                          <Grid2 container spacing={2}>
                            <Grid2 {...{ xs: 6, md: 2 }}>
                              <Stack
                                direction='row'
                                alignItems='center'
                                height='100%'
                              >
                                <Typography>SMS</Typography>
                                <ThemeProvider theme={DefaultSwitch}>
                                  <FormControlLabel
                                    control={
                                      <Controller
                                        name='sms'
                                        control={control}
                                        render={({ field }) => (
                                          <Switch
                                            {...field}
                                            checked={field.value}
                                          />
                                        )}
                                      />
                                    }
                                    label=''
                                    labelPlacement='start'
                                  />
                                </ThemeProvider>
                              </Stack>
                            </Grid2>
                            <Grid2 {...{ xs: 6, md: 2 }}>
                              <Stack
                                direction='row'
                                alignItems='center'
                                height='100%'
                              >
                                <Typography>Email</Typography>
                                <ThemeProvider theme={DefaultSwitch}>
                                  <FormControlLabel
                                    control={
                                      <Controller
                                        name='email'
                                        control={control}
                                        render={({ field }) => (
                                          <Switch
                                            {...field}
                                            checked={field.value}
                                          />
                                        )}
                                      />
                                    }
                                    label=''
                                    labelPlacement='start'
                                  />
                                </ThemeProvider>
                              </Stack>
                            </Grid2>
                            <Grid2 {...{ xs: 12, md: 2 }}>
                              <AutoCompleteController
                                control={control}
                                errors={errors}
                                label='VIP'
                                name='vipLevel'
                                options={
                                  bookingOptionList?.property?.VIPLevels || []
                                }
                                isOptionEqualToValue={(
                                  option: { vip_level: string },
                                  value: { vip_level: string },
                                ) => option.vip_level === value.vip_level}
                                sx={{ margin: 0, flex: 1 }}
                                getOptionLabel={(option: any) =>
                                  option.vip_level
                                }
                                borderErrorOnly
                                style={{ margin: 0 }}
                              />
                            </Grid2>
                            <Grid2 {...{ xs: 12, md: 6 }}>
                              <AutoCompleteController
                                control={control}
                                errors={errors}
                                label='Account Owner'
                                name='accountOwner'
                                options={[]}
                                borderErrorOnly
                                style={{ margin: 0 }}
                              />
                            </Grid2>
                          </Grid2>
                        </Stack>
                      </Box>

                      {/* ADDRESS DETAILS */}
                      <Box>
                        <Grid2 {...{ xs: 12 }} container gap={1}>
                          <Grid2 {...{ xs: 12 }}>
                            <GroupTitle title='Address' />
                          </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                          <Grid2 {...{ xs: 12, md: 6 }}>
                            <InputFieldsController
                              label='Home Address'
                              name='homeAddress'
                              control={control}
                              errors={errors}
                              borderErrorOnly
                              wrapperStyle={{
                                marginBottom: 0,
                              }}
                            />
                          </Grid2>
                          <Grid2 {...{ xs: 12, md: 3 }}>
                            <InputFieldsController
                              control={control}
                              errors={errors}
                              label='City/Province'
                              name='cityAddress'
                              borderErrorOnly
                              wrapperStyle={{
                                marginBottom: 0,
                              }}
                            />
                          </Grid2>
                          <Grid2 {...{ xs: 12, md: 3 }}>
                            <AutoCompleteController
                              control={control}
                              errors={errors}
                              label='Country *'
                              name='country'
                              options={nationalityList || []}
                              getOptionLabel={(option: any) => option.code}
                              borderErrorOnly
                              style={{ margin: 0 }}
                            />
                          </Grid2>
                        </Grid2>
                      </Box>

                      {/* COMPANY DETAILS */}
                      <Box>
                        <Grid2 {...{ xs: 12 }} container gap={1}>
                          <Grid2 {...{ xs: 12 }}>
                            <GroupTitle title='Company Details' />
                          </Grid2>
                        </Grid2>
                        <Grid2 container spacing={2}>
                          <Grid2 {...{ xs: 12, md: 6 }}>
                            <AutoCompleteController
                              control={control}
                              errors={errors}
                              label='Company'
                              name='company'
                              options={optionCompany || []}
                              onChange={handleCompanyChange}
                              textFieldProps={{
                                inputProps: { maxLength: 150 },
                                InputProps: {
                                  startAdornment: (
                                    <InputAdornment position='start'>
                                      <IconButton
                                        sx={{ p: 0 }}
                                        onClick={
                                          profileCompanyDrawerState.onOpen
                                        }
                                      >
                                        <Search />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                },
                              }}
                              borderErrorOnly
                              style={{ margin: 0 }}
                            />
                          </Grid2>
                          <Grid2 {...{ xs: 12, md: 3 }}>
                            <InputFieldsController
                              label='Department'
                              name='department'
                              control={control}
                              errors={errors}
                              borderErrorOnly
                              wrapperStyle={{
                                marginBottom: 0,
                              }}
                              inputProps={{ maxLength: 100 }}
                            />
                          </Grid2>
                          <Grid2 {...{ xs: 12, md: 3 }}>
                            <InputFieldsController
                              label='Designation'
                              name='designation'
                              control={control}
                              errors={errors}
                              borderErrorOnly
                              wrapperStyle={{
                                marginBottom: 0,
                              }}
                              inputProps={{ maxLength: 100 }}
                            />
                          </Grid2>
                        </Grid2>
                      </Box>

                      {/* GUEST REMARKS */}
                      <Box>
                        <Grid2 {...{ xs: 12 }} container gap={1}>
                          <Grid2 {...{ xs: 12 }}>
                            <TabContext value={tabValue}>
                              <Stack gap={2}>
                                <TabList onChange={handleTabChange}>
                                  <Tab
                                    label='Guest Remarks'
                                    value='guest_remarks'
                                    sx={{
                                      textTransform: 'capitalize',
                                      fontSize: '14px',
                                    }}
                                  />
                                  <Tab
                                    label='Business Info'
                                    value='business_info'
                                    sx={{
                                      textTransform: 'capitalize',
                                      fontSize: '14px',
                                    }}
                                  />
                                </TabList>
                                <TabPanel value='guest_remarks' sx={{ p: '0' }}>
                                  <Grid2 container spacing={2}>
                                    <Grid2 {...{ xs: 12, md: 12 }}>
                                      <InputFieldsController
                                        label='Guest Remarks'
                                        name='guestRemarks'
                                        placeholder=' '
                                        control={control}
                                        errors={errors}
                                        inputProps={{
                                          minLength: 0,
                                          maxLength: 255,
                                        }}
                                        multiline
                                        rows={3.6}
                                        borderErrorOnly
                                        wrapperStyle={{
                                          marginBottom: 0,
                                        }}
                                      />
                                    </Grid2>
                                  </Grid2>
                                </TabPanel>
                                <TabPanel
                                  value='business_info'
                                  sx={{
                                    p: '0',
                                    gap: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                  }}
                                >
                                  <Grid2 container spacing={2}>
                                    <Grid2 {...{ xs: 8, md: 8 }}>
                                      <InputFieldsController
                                        label='Tax Identification Number'
                                        name='tinNo'
                                        placeholder='000-000-0000'
                                        control={control}
                                        errors={errors}
                                        inputProps={{
                                          minLength: 12,
                                          maxLength: 20,
                                        }}
                                        borderErrorOnly
                                        wrapperStyle={{
                                          marginBottom: 0,
                                        }}
                                        InputProps={{
                                          inputComponent: CustomMaskFormat,
                                          inputProps: {
                                            mask: '000-000-000-000000',
                                            definitions: {
                                              '0': /[0-9]/,
                                            },
                                            placeholder: '000-000-000-00000',
                                            overwrite: true,
                                          },
                                        }}
                                      />
                                    </Grid2>
                                    <Grid2 {...{ xs: 4, md: 4 }}>
                                      <AutoCompleteController
                                        control={control}
                                        errors={errors}
                                        label='ATC'
                                        name='taxCode'
                                        options={taxCodeList || []}
                                        onChange={handleTaxChange}
                                        borderErrorOnly
                                        style={{ margin: 0 }}
                                      />
                                    </Grid2>
                                  </Grid2>
                                  <Grid2 container>
                                    <Grid2 {...{ xs: 8, md: 8 }}>
                                      <InputFieldsController
                                        label='Business Style'
                                        name='businessStyle'
                                        placeholder=''
                                        control={control}
                                        errors={errors}
                                        inputProps={{
                                          maxLength: 100,
                                        }}
                                        borderErrorOnly
                                        wrapperStyle={{
                                          marginBottom: 0,
                                        }}
                                      />
                                    </Grid2>
                                    <Grid2 {...{ xs: 4, md: 4 }} spacing={2}>
                                      <Stack
                                        direction='row'
                                        alignItems='center'
                                        justifyContent='flex-start'
                                        height='100%'
                                        gap={1}
                                      >
                                        <ThemeProvider theme={DefaultSwitch}>
                                          <FormControlLabel
                                            control={
                                              <Controller
                                                name='salesToGov'
                                                control={control}
                                                render={({ field }) => (
                                                  <Switch
                                                    {...field}
                                                    checked={field.value}
                                                    sx={{}}
                                                  />
                                                )}
                                              />
                                            }
                                            label=''
                                            labelPlacement='start'
                                          />
                                        </ThemeProvider>
                                        <Typography>
                                          Sales To Government
                                        </Typography>
                                      </Stack>
                                    </Grid2>
                                  </Grid2>
                                </TabPanel>
                              </Stack>
                            </TabContext>
                          </Grid2>
                        </Grid2>
                      </Box>
                    </Stack>
                  </Grid2>
                </Grid2>
              </Stack>

              {/* FORM BUTTON */}
              <Box
                sx={{
                  position: 'sticky',
                  bottom: '0',
                  bgcolor: paperBg,
                  zIndex: 20,
                }}
              >
                <Divider
                  sx={{
                    borderStyle: 'dashed',
                  }}
                />
                <Stack
                  sx={{ p: '24px 32px 24px 24px' }}
                  direction={{ xs: 'column', sm: 'row' }}
                  gap={{ xs: 2, lg: 1 }}
                  justifyContent='flex-end'
                >
                  <Button
                    variant='outlined'
                    onClick={handleDrawerClose}
                    sx={{
                      width: { xs: '100%', md: '100px' },
                      height: '35px',
                      border: '1px solid',
                      backgroundColor: 'transparent',
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    form='individual-profile'
                    onClick={handleSubmit(handleIndividualProfile)}
                    variant='contained'
                    sx={{
                      width: { xs: '100%', md: '100px' },
                    }}
                    disabled={
                      isSaveDisabled(errors) || isLoading || isUploadLoading
                    }
                  >
                    Save
                  </Button>
                </Stack>
              </Box>
            </Stack>

            <Divider
              orientation='vertical'
              flexItem
              sx={{ display: { xs: 'none', sm: 'block' } }}
            />
            <Divider
              orientation='horizontal'
              flexItem
              sx={{ display: { xs: 'block', sm: 'none' } }}
            />

            {/* SIDEBAR BUTTON */}
            <Stack
              direction={{ xs: 'row', sm: 'column' }}
              sx={{
                width: { xs: '100%', sm: '73px' },
                height: { xs: '76px', sm: '100%' },
                p: '12px',
                gap: '12px',
                overflowX: { xs: 'auto', md: 'hidden' },
                minHeight: { xs: '76px', lg: '100%' },
                overflowY: { xs: 'auto', md: 'hidden' },
                ':hover': {
                  overflowY: 'auto',
                },
              }}
            >
              <FOFormSidebarBtn
                onClick={roomPostingDisclosure.onOpen}
                icon={PersonPinOutlined}
                title='Account Posting'
                isActive={
                  roomPostingDisclosure.isOpen &&
                  !isModalOpenVisitHistory &&
                  !isModalOpenLogs &&
                  !tracesDisclosure.isOpen
                }
                disabled={
                  !profileID ||
                  guestBalance === '0.00' ||
                  currentModule !== 'FO'
                }
              />
              <FOFormSidebarBtn
                onClick={handleOpenModalVisitHistory}
                icon={HistoryOutlined}
                title='Visit History'
                isActive={
                  !roomPostingDisclosure.isOpen &&
                  isModalOpenVisitHistory &&
                  !isModalOpenLogs &&
                  !tracesDisclosure.isOpen
                }
                disabled={!profileID}
              />
              <FOFormSidebarBtn
                onClick={tracesDisclosure.onOpen}
                icon={TrackChangesOutlined}
                title='Traces'
                isActive={
                  !roomPostingDisclosure.isOpen &&
                  !isModalOpenVisitHistory &&
                  !isModalOpenLogs &&
                  tracesDisclosure.isOpen
                }
                disabled={!profileID}
              />
              <FOFormSidebarBtn
                onClick={handleOpenModalLogs}
                icon={ListAlt}
                title='Logs'
                isActive={
                  !roomPostingDisclosure.isOpen &&
                  !isModalOpenVisitHistory &&
                  isModalOpenLogs &&
                  !tracesDisclosure.isOpen
                }
                disabled={!profileID}
              />
              <FOFormSidebarBtn
                id='print-btn'
                onClick={(e) => {
                  handleDisclosureClick('printDisclosure');
                  handleClickPrintOptions(e);
                }}
                icon={PrintOutlined}
                title='Print'
                isActive={
                  printDisclosure.isOpen &&
                  !roomPostingDisclosure.isOpen &&
                  !isModalOpenVisitHistory &&
                  !isModalOpenLogs &&
                  !tracesDisclosure.isOpen
                }
                disabled={!profileID}
              />
              <CustomWidthTooltip
                title={
                  !profileID || guestBalance !== '0.00'
                    ? 'All balances must be settled before deleting this profile.'
                    : ''
                }
                placement='left'
                arrow
                sx={{ m: 2 }}
              >
                <span>
                  <FOFormSidebarBtn
                    onClick={deleteProfileDisclosure.onOpen}
                    icon={NoAccounts}
                    title={
                      !profileID || guestBalance !== '0.00'
                        ? ''
                        : 'Delete Profile'
                    }
                    isActive={
                      !roomPostingDisclosure.isOpen &&
                      !isModalOpenVisitHistory &&
                      isModalOpenLogs &&
                      !tracesDisclosure.isOpen
                    }
                    // disabled={!profileID || guestBalance !== '0.00'}
                    disabled={true}
                    sxIcon={{
                      color: gray,
                      // !profileID || guestBalance !== '0.00' ? gray : danger,
                    }}
                  />
                </span>
              </CustomWidthTooltip>
              <DialogInformation />
            </Stack>
            {useMemo(
              () => (
                <DeleteProfileDialog
                  open={deleteProfileDisclosure.isOpen}
                  onConfirm={async () => {
                    //Inject print code here
                    deleteProfileDisclosure.onClose();
                    handleDeleteProfile();
                  }}
                  desc={
                    <Stack gap='8px' sx={{ maxWidth: '405px' }}>
                      <Typography>
                        {hasVisits
                          ? 'This profile is currently in use. Deleting it will result in all associated data being retracted permanently. This action cannot be undone.'
                          : 'Deleting it will result in all associated data being retracted permanently. This action cannot be undone.'}
                      </Typography>
                    </Stack>
                  }
                  title='Are you sure you want to delete this profile?'
                  cancelButtonTitle='Cancel'
                  confirmButtonTitle='Proceed'
                  icon={ReportProblem}
                  iconColor={'warning'}
                  onClose={() => deleteProfileDisclosure.onClose()}
                />
              ),
              [deleteProfileDisclosure],
            )}
          </Stack>
        </DrawerWrapper>
      </FormProvider>

      <>
        {/* PRINT */}
        <Menu
          id='print-menu'
          anchorEl={anchorElPrint}
          keepMounted
          open={Boolean(anchorElPrint) && !sidebarBtnLoadingState.isOpen}
          onClose={handleClosePrintOptions}
        >
          <MenuItem
            key='guest-data'
            onClick={async () => {
              sidebarBtnLoadingState.onOpen();
              collectionsDisclosure.onOpen();
              handleClosePrintOptions();
              sidebarBtnLoadingState.onClose();
            }}
          >
            Guest Information Request
          </MenuItem>
        </Menu>
        <ContactNumberListModal
          isOpen={isModalOpenContact}
          onClose={handleCloseModalContact}
          profileID={profileID}
        />
        <EmailListModal
          isOpen={isModalOpenEmail}
          onClose={handleCloseModalEmail}
          profileID={profileID}
        />
        <ProfileTakePhotoModal
          isOpen={isModalOpenTakePhoto}
          onClose={handleCloseModalTakePhoto}
          onCapture={async (fileData, fileUrl, fileName) => {
            setFileData(fileData);
            setFileName(fileName);
            setFileUrl(fileUrl);
            setIsPhotoExisting(false);
            setTriggerFileLoading(true);
          }}
        />
        <FOProfileGroupDrawer
          isOpen={profileCompanyDrawerState.isOpen}
          onClose={() => {
            profileCompanyDrawerState.onClose();
          }}
          profileID={companyProfileId}
        />
        <ProfileVisitHistoryModal
          isOpen={isModalOpenVisitHistory}
          onClose={handleCloseModalVisitHistory}
          profileID={profileID}
        />
        <ProfileLogsModal
          isOpen={isModalOpenLogs}
          onClose={handleCloseModalLogs}
          profileID={profileID}
        />
        <TracesListModal
          referenceID={profileID}
          isOpen={tracesDisclosure.isOpen}
          onClose={tracesDisclosure.onClose}
          traceTarget='Profile'
        />
        <FORoomPostingContainer
          transactionDetails={{
            folioUuid: profileID || '',
            folioStatus: '',
          }}
          onCloseModal={() => {
            roomPostingDisclosure.onClose();
          }}
          isOpenModal={roomPostingDisclosure.isOpen}
          transactionType={'Post Master'}
          isCityLedgerPosting={roomPostingDisclosure.isOpen}
        />
        <DialogComponentV2
          open={collectionsDisclosure.isOpen}
          onConfirm={async () => {
            setIncludeCollections(true);
            printGuestData.onOpen();
            collectionsDisclosure.onClose();
          }}
          title='Include Collections'
          desc='Do you want to include collections?'
          switchButton
          cancelButtonTitle='No'
          confirmButtonTitle='Yes'
          onClose={async () => {
            setIncludeCollections(false);
            printGuestData.onOpen();
            collectionsDisclosure.onClose();
          }}
          icon={HideSourceOutlined}
        />
        {/* Guest Information Request */}

        <PDFGuestDataForm
          {...{
            profileID: profileID as string | undefined,
            includeCollections: includeCollections,
            isOpen: printGuestData.isOpen,
            handleModalClose: printGuestData.onClose,
          }}
        />
      </>
    </>
  );
};

export default memo(FOProfileIndividualDrawer);
